import React, { useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExclamationTriangle,
  FaPlus,
  FaRedo,
  FaTrash,
} from "react-icons/fa";
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
const COLORS = {
  bg: "#F6F4F0",
  ink: "#17161B",
  panel: "#FFFFFF",
  dark: "#111014",
  accent: "#E8481D",
  accentSoft: "#FCE7DF",
  green: "#16845B",
  greenSoft: "#E7F6EF",
  yellow: "#A76500",
  yellowSoft: "#FFF4D6",
  muted: "#77737F",
  border: "#E7E3DC",
};

const PRESETS = {
  nextjs: {
    label: "Next.js",
    stack: "Next.js App Router, React, JavaScript/TypeScript, and Tailwind CSS",
    packageManager: "npm",
    install: "npm install",
    dev: "npm run dev",
    build: "npm run build",
    test: "npm test",
    lint: "npm run lint",
    typecheck: "npx tsc --noEmit",
    layout: [
      "app/ — routes, layouts, pages, and server actions",
      "components/ — reusable UI components",
      "lib/ — shared utilities and integrations",
      "public/ — static assets",
    ],
    conventions: [
      "Prefer Server Components unless client-side state or browser APIs are required.",
      "Add `use client` only to the smallest necessary component boundary.",
      "Use the existing styling system and component patterns.",
    ],
  },
  react: {
    label: "React",
    stack: "React, JavaScript/TypeScript, React Router, and the existing styling system",
    packageManager: "npm",
    install: "npm install",
    dev: "npm start",
    build: "npm run build",
    test: "npm test",
    lint: "npm run lint",
    typecheck: "npx tsc --noEmit",
    layout: [
      "src/components/ — reusable UI components",
      "src/pages/ — route-level screens",
      "src/hooks/ — shared React hooks",
      "src/services/ — API clients and external integrations",
    ],
    conventions: [
      "Use functional components and hooks.",
      "Keep state close to the component that owns it.",
      "Follow existing component, routing, and styling patterns.",
    ],
  },
  node: {
    label: "Node / Express",
    stack: "Node.js, Express, and JavaScript/TypeScript",
    packageManager: "npm",
    install: "npm install",
    dev: "npm run dev",
    build: "npm run build",
    test: "npm test",
    lint: "npm run lint",
    typecheck: "npx tsc --noEmit",
    layout: [
      "controller/ — request handlers and application logic",
      "route/ — Express route definitions",
      "middleware/ — shared request middleware",
      "utils/ — reusable helpers",
    ],
    conventions: [
      "Keep route files thin and move business logic into controllers or services.",
      "Return consistent JSON response shapes and appropriate HTTP status codes.",
      "Validate untrusted input before using it.",
    ],
  },
  wordpress: {
    label: "WordPress",
    stack: "WordPress, PHP, JavaScript, HTML, and CSS",
    packageManager: "npm",
    install: "npm install",
    dev: "npm run dev",
    build: "npm run build",
    test: "npm test",
    lint: "npm run lint",
    typecheck: "",
    layout: [
      "functions.php — theme hooks and registrations",
      "template-parts/ — reusable templates",
      "assets/ — CSS, JavaScript, images, and fonts",
      "inc/ — reusable PHP modules",
    ],
    conventions: [
      "Follow WordPress coding standards and escape rendered output.",
      "Sanitize and validate all external input.",
      "Use hooks instead of modifying WordPress core files.",
    ],
  },
  python: {
    label: "Python",
    stack: "Python and the repository's existing framework and tooling",
    packageManager: "pip",
    install: "pip install -r requirements.txt",
    dev: "python -m app",
    build: "",
    test: "pytest",
    lint: "ruff check .",
    typecheck: "mypy .",
    layout: [
      "src/ — application source code",
      "tests/ — automated tests",
      "scripts/ — development and maintenance scripts",
      "docs/ — project documentation",
    ],
    conventions: [
      "Follow existing type-hint and module organization patterns.",
      "Keep functions focused and make side effects explicit.",
      "Add or update tests for behavior changes.",
    ],
  },
  custom: {
    label: "Custom",
    stack: "",
    packageManager: "",
    install: "",
    dev: "",
    build: "",
    test: "",
    lint: "",
    typecheck: "",
    layout: ["src/ — primary source code"],
    conventions: ["Follow existing repository patterns unless the task explicitly requires a change."],
  },
};

let rowId = 0;
const makeRow = (value = "") => ({ id: `row-${Date.now()}-${++rowId}`, value });
const toRows = (values) => values.map((value) => makeRow(value));
const DEFAULT_DO_NOTS = [
  "Do not commit secrets, credentials, generated build output, or local environment files.",
  "Do not change unrelated behavior, formatting, or dependencies.",
  "Do not use destructive Git commands unless the user explicitly requests them.",
];
const DEFAULT_VERIFICATION = [
  "Run the relevant lint, test, typecheck, and build commands.",
  "Review the final diff for unintended changes.",
  "Report any check that could not be run and explain why.",
];
const DEFAULT_REVIEW_RULES = [
  "Prioritize correctness, regressions, security, and missing tests.",
  "Keep findings specific and cite the affected file or behavior.",
];

const AgentsMdGenerator = () => {
  const initialPreset = PRESETS.nextjs;
  const [presetKey, setPresetKey] = useState("nextjs");
  const [projectName, setProjectName] = useState("");
  const [summary, setSummary] = useState("");
  const [scopeType, setScopeType] = useState("root");
  const [scopePath, setScopePath] = useState("");
  const [fileType, setFileType] = useState("AGENTS.md");
  const [stack, setStack] = useState(initialPreset.stack);
  const [packageManager, setPackageManager] = useState(initialPreset.packageManager);
  const [installCommand, setInstallCommand] = useState(initialPreset.install);
  const [devCommand, setDevCommand] = useState(initialPreset.dev);
  const [buildCommand, setBuildCommand] = useState(initialPreset.build);
  const [testCommand, setTestCommand] = useState(initialPreset.test);
  const [lintCommand, setLintCommand] = useState(initialPreset.lint);
  const [typecheckCommand, setTypecheckCommand] = useState(initialPreset.typecheck);
  const [layoutRows, setLayoutRows] = useState(toRows(initialPreset.layout));
  const [conventionRows, setConventionRows] = useState(toRows(initialPreset.conventions));
  const [doNotRows, setDoNotRows] = useState(toRows(DEFAULT_DO_NOTS));
  const [verificationRows, setVerificationRows] = useState(toRows(DEFAULT_VERIFICATION));
  const [reviewRows, setReviewRows] = useState(toRows(DEFAULT_REVIEW_RULES));
  const [generated, setGenerated] = useState(false);

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    setPresetKey(key);
    setStack(preset.stack);
    setPackageManager(preset.packageManager);
    setInstallCommand(preset.install);
    setDevCommand(preset.dev);
    setBuildCommand(preset.build);
    setTestCommand(preset.test);
    setLintCommand(preset.lint);
    setTypecheckCommand(preset.typecheck);
    setLayoutRows(toRows(preset.layout));
    setConventionRows(toRows(preset.conventions));
    setGenerated(false);
    console.log("[AGENTS GENERATOR] Preset applied:", key);
  };

  const updateRow = (setter, id, value) =>
    setter((rows) => rows.map((row) => (row.id === id ? { ...row, value } : row)));
  const addRow = (setter) => setter((rows) => [...rows, makeRow("")]);
  const removeRow = (setter, id) => setter((rows) => rows.filter((row) => row.id !== id));

  const validValues = (rows) => rows.map((row) => row.value.trim()).filter(Boolean);

  const output = useMemo(() => {
    const name = projectName.trim() || "Project";
    const scope =
      scopeType === "root"
        ? "These instructions apply to the entire repository."
        : `These instructions apply to \`${scopePath.trim() || "this directory"}\` and its descendants.`;
    const commands = [
      ["Install", installCommand],
      ["Development", devCommand],
      ["Build", buildCommand],
      ["Test", testCommand],
      ["Lint", lintCommand],
      ["Typecheck", typecheckCommand],
    ].filter(([, command]) => command.trim());
    const sections = [`# ${name} — Agent Instructions`, "", "## Scope", "", scope];

    sections.push("", "## Project Overview", "");
    sections.push(`- **Purpose:** ${summary.trim() || "Describe the project's purpose before relying on this file."}`);
    if (stack.trim()) sections.push(`- **Primary stack:** ${stack.trim()}`);
    if (packageManager.trim()) sections.push(`- **Package manager:** ${packageManager.trim()}`);

    const layout = validValues(layoutRows);
    if (layout.length) {
      sections.push("", "## Repository Map", "", ...layout.map((item) => `- ${item}`));
    }

    if (commands.length) {
      sections.push("", "## Setup and Commands", "");
      commands.forEach(([label, command]) => sections.push(`- **${label}:** \`${command.trim()}\``));
    }

    const conventions = validValues(conventionRows);
    if (conventions.length) {
      sections.push("", "## Engineering Conventions", "", ...conventions.map((item) => `- ${item}`));
    }

    sections.push(
      "",
      "## Change Guidelines",
      "",
      "- Read the relevant files completely before editing.",
      "- Preserve existing behavior, public APIs, and design unless the task explicitly requires a change.",
      "- Make the smallest coherent change that fully solves the request.",
      "- Reuse existing utilities, components, and patterns before introducing new abstractions.",
      "- Add comments only where the intent is not clear from the code itself."
    );

    const doNots = validValues(doNotRows);
    if (doNots.length) {
      sections.push("", "## Safety and Do-Not Rules", "", ...doNots.map((item) => `- ${item}`));
    }

    const verification = validValues(verificationRows);
    if (verification.length) {
      sections.push("", "## Validation and Definition of Done", "", ...verification.map((item) => `- ${item}`));
    }

    const reviews = validValues(reviewRows);
    if (reviews.length) {
      sections.push("", "## Code Review Rules", "", ...reviews.map((item) => `- ${item}`));
    }

    return `${sections.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
  }, [projectName, summary, scopeType, scopePath, stack, packageManager, installCommand, devCommand, buildCommand, testCommand, lintCommand, typecheckCommand, layoutRows, conventionRows, doNotRows, verificationRows, reviewRows]);

  const byteSize = useMemo(() => new Blob([output]).size, [output]);
  const warnings = useMemo(() => {
    const items = [];
    if (!projectName.trim()) items.push("Project name is empty; the fallback name “Project” is being used.");
    if (!summary.trim()) items.push("Project purpose is still a placeholder.");
    if (!testCommand.trim()) items.push("No test command is configured.");
    if (scopeType === "nested" && !scopePath.trim()) items.push("Nested scope is selected but no directory path is provided.");
    if (byteSize > 32768) items.push("This file exceeds the default 32 KiB combined project-instruction limit.");
    return items;
  }, [projectName, summary, testCommand, scopeType, scopePath, byteSize]);

  const handleGenerate = () => {
    setGenerated(true);
    console.log("[AGENTS GENERATOR] File generated.", {
      fileType,
      preset: presetKey,
      scopeType,
      scopePath,
      characters: output.length,
      bytes: byteSize,
      warnings: warnings.length,
    });
    toast.success(`${fileType} generated!`, { theme: "colored" });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      toast.success("AGENTS instructions copied!", { theme: "colored" });
    } catch (error) {
      console.error("[AGENTS GENERATOR] Copy failed:", error);
      toast.error("Failed to copy the file.");
    }
  };

  const handleDownload = () => {
    const blob = new Blob([output], { type: "text/markdown;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileType;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    toast.success(`${fileType} downloaded!`, { theme: "colored" });
  };

  const handleReset = () => {
    setProjectName("");
    setSummary("");
    setScopeType("root");
    setScopePath("");
    setFileType("AGENTS.md");
    applyPreset("nextjs");
    setDoNotRows(toRows(DEFAULT_DO_NOTS));
    setVerificationRows(toRows(DEFAULT_VERIFICATION));
    setReviewRows(toRows(DEFAULT_REVIEW_RULES));
    setGenerated(false);
    toast.info("Generator reset.");
  };

  const RowEditor = ({ rows, setter, placeholder }) => (
    <div>
      {rows.map((row) => (
        <div key={row.id} style={{ display: "flex", gap: 7, marginBottom: 7 }}>
          <input className="form-control form-control-sm" value={row.value} onChange={(event) => updateRow(setter, row.id, event.target.value)} placeholder={placeholder} />
          <button type="button" onClick={() => removeRow(setter, row.id)} className="btn btn-sm btn-outline-danger" aria-label="Remove row"><FaTrash size={10} /></button>
        </div>
      ))}
      <button type="button" onClick={() => addRow(setter)} className="btn btn-sm btn-outline-secondary"><FaPlus size={10} /> Add rule</button>
    </div>
  );

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", padding: "42px 16px" }}>
      <ToastContainer />
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
       

        <div className="row g-3">
          <div className="col-lg-7">
            <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 15, padding: 22 }}>
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label small fw-bold">Project name</label><input className="form-control" value={projectName} onChange={(event) => { setProjectName(event.target.value); setGenerated(false); }} placeholder="e.g. Microters Tools" /></div>
                <div className="col-md-6"><label className="form-label small fw-bold">Project preset</label><select className="form-select" value={presetKey} onChange={(event) => applyPreset(event.target.value)}>{Object.entries(PRESETS).map(([key, value]) => <option value={key} key={key}>{value.label}</option>)}</select></div>
                <div className="col-12"><label className="form-label small fw-bold">Project purpose</label><textarea className="form-control" rows={2} value={summary} onChange={(event) => { setSummary(event.target.value); setGenerated(false); }} placeholder="What does this repository build or maintain?" /></div>
                <div className="col-md-4"><label className="form-label small fw-bold">File type</label><select className="form-select" value={fileType} onChange={(event) => setFileType(event.target.value)}><option>AGENTS.md</option><option>AGENTS.override.md</option></select></div>
                <div className="col-md-4"><label className="form-label small fw-bold">Scope</label><select className="form-select" value={scopeType} onChange={(event) => setScopeType(event.target.value)}><option value="root">Repository root</option><option value="nested">Nested directory</option></select></div>
                <div className="col-md-4"><label className="form-label small fw-bold">Directory path</label><input className="form-control" value={scopePath} disabled={scopeType === "root"} onChange={(event) => setScopePath(event.target.value)} placeholder="services/api/" /></div>
                <div className="col-md-8"><label className="form-label small fw-bold">Primary stack</label><input className="form-control" value={stack} onChange={(event) => setStack(event.target.value)} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold">Package manager</label><input className="form-control" value={packageManager} onChange={(event) => setPackageManager(event.target.value)} placeholder="npm, pnpm, pip..." /></div>
              </div>

              <hr style={{ borderColor: COLORS.border, margin: "20px 0" }} />
              <h6 style={{ fontWeight: 800 }}>Commands</h6>
              <div className="row g-2">
                {[["Install", installCommand, setInstallCommand], ["Development", devCommand, setDevCommand], ["Build", buildCommand, setBuildCommand], ["Test", testCommand, setTestCommand], ["Lint", lintCommand, setLintCommand], ["Typecheck", typecheckCommand, setTypecheckCommand]].map(([label, value, setter]) => (
                  <div className="col-md-6" key={label}><label className="form-label small">{label}</label><input className="form-control form-control-sm" value={value} onChange={(event) => setter(event.target.value)} placeholder={`${label.toLowerCase()} command`} /></div>
                ))}
              </div>

              <hr style={{ borderColor: COLORS.border, margin: "20px 0" }} />
              <div className="row g-4">
                <div className="col-md-6"><h6 style={{ fontWeight: 800 }}>Repository map</h6><RowEditor rows={layoutRows} setter={setLayoutRows} placeholder="path/ — purpose" /></div>
                <div className="col-md-6"><h6 style={{ fontWeight: 800 }}>Engineering conventions</h6><RowEditor rows={conventionRows} setter={setConventionRows} placeholder="Convention or preferred pattern" /></div>
                <div className="col-md-6"><h6 style={{ fontWeight: 800 }}>Safety and do-not rules</h6><RowEditor rows={doNotRows} setter={setDoNotRows} placeholder="Action the agent must avoid" /></div>
                <div className="col-md-6"><h6 style={{ fontWeight: 800 }}>Definition of done</h6><RowEditor rows={verificationRows} setter={setVerificationRows} placeholder="Required verification" /></div>
                <div className="col-12"><h6 style={{ fontWeight: 800 }}>Code review rules</h6><RowEditor rows={reviewRows} setter={setReviewRows} placeholder="Review expectation" /></div>
              </div>

              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 22 }}>
                <button type="button" onClick={handleGenerate} style={{ border: "none", borderRadius: 9, padding: "11px 18px", background: COLORS.accent, color: "#fff", fontWeight: 750 }}>Generate {fileType}</button>
                <button type="button" onClick={handleReset} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 9, padding: "11px 15px", background: "#fff", color: COLORS.ink, fontWeight: 700 }}><FaRedo size={11} /> Reset</button>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div style={{ background: COLORS.dark, borderRadius: 15, overflow: "hidden", position: "sticky", top: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.09)", padding: "11px 14px" }}>
                <div><span style={{ color: "#ECE9E2", fontSize: 12, fontWeight: 800 }}>{fileType}</span><span style={{ color: byteSize > 32768 ? "#F3C969" : "#83E2BB", fontSize: 11, marginLeft: 8 }}>{byteSize.toLocaleString()} bytes</span></div>
                <div style={{ display: "flex", gap: 7 }}><button onClick={handleCopy} className="btn btn-sm" style={{ color: "#ECE9E2", background: "rgba(255,255,255,0.08)" }}><FaCopy size={10} /> Copy</button><button onClick={handleDownload} className="btn btn-sm" style={{ color: "#fff", background: COLORS.accent }}><FaDownload size={10} /> Download</button></div>
              </div>

              {warnings.length > 0 ? (
                <div style={{ background: "rgba(167,101,0,0.16)", color: "#F3C969", padding: "10px 14px", fontSize: 11.5 }}>
                  {warnings.map((warning) => <div key={warning} style={{ marginBottom: 4 }}><FaExclamationTriangle size={9} style={{ marginRight: 6 }} />{warning}</div>)}
                </div>
              ) : generated ? (
                <div style={{ background: "rgba(22,132,91,0.16)", color: "#83E2BB", padding: "10px 14px", fontSize: 11.5 }}><FaCheckCircle size={10} style={{ marginRight: 6 }} />Ready to add to your repository.</div>
              ) : null}

              <pre style={{ margin: 0, padding: 16, color: "#ECE9E2", fontSize: 11.5, lineHeight: 1.55, maxHeight: "72vh", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{output}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsMdGenerator;
